<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import PipelineStepForm from './PipelineStepForm.vue';
import PipelinePermissions from './PipelinePermissions.vue';

const props = defineProps({
  pipeline: {
    type: Object,
    default: null,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'save']);

const { t } = useI18n();
const store = useStore();

// Multi-step state
const currentStep = ref(1);
const totalSteps = 3;

// Form data
const formData = ref({
  name: '',
  description: '',
  columns: [],
  permissions: {
    agentIds: [],
    isPublic: false,
  },
});

// Initialize default columns for new pipelines (matching backend KanbanColumn.default_columns)
const initializeDefaultColumns = () => {
  if (!props.isEdit && formData.value.columns.length === 0) {
    formData.value.columns = [
      {
        name: 'Novos Leads',
        description: 'Conversas iniciais e novos contatos',
        color: '#3B82F6',
        position: 0,
        tempId: Date.now(),
      },
      {
        name: 'Qualificação',
        description: 'Avaliando interesse e necessidade',
        color: '#F59E0B',
        position: 1,
        tempId: Date.now() + 1,
      },
      {
        name: 'Proposta',
        description: 'Enviando propostas e orçamentos',
        color: '#8B5CF6',
        position: 2,
        tempId: Date.now() + 2,
      },
      {
        name: 'Negociação',
        description: 'Fechando negócio',
        color: '#EF4444',
        position: 3,
        tempId: Date.now() + 3,
      },
      {
        name: 'Fechado',
        description: 'Venda concluída',
        color: '#10B981',
        position: 4,
        tempId: Date.now() + 4,
      },
    ];
  }
};

// Initialize form data
const initializeFormData = () => {
  if (props.pipeline) {
    formData.value.name = props.pipeline.name || '';
    formData.value.description = props.pipeline.description || '';
    // Garantir que as colunas existentes tenham os campos necessários
    formData.value.columns = (props.pipeline.kanban_columns || []).map(col => ({
      id: col.id,
      name: col.name,
      description: col.description || '',
      color: col.color || '#3B82F6',
      position: col.position || 0,
    }));
  } else {
    formData.value.name = '';
    formData.value.description = '';
    formData.value.columns = [];
  }

  formData.value.permissions = {
    agentIds: [],
    isPublic: false,
  };

  // Initialize default columns for new pipelines
  initializeDefaultColumns();
};

// Watch for pipeline changes
watch(
  () => props.pipeline,
  () => {
    initializeFormData();
  },
  { immediate: true }
);

// Validation
const errors = ref({});

const stepTitles = computed(() => [
  t('KANBAN.PIPELINE_MODAL.STEPS.BASIC_INFO'),
  t('KANBAN.PIPELINE_MODAL.STEPS.STAGES'),
  t('KANBAN.PIPELINE_MODAL.STEPS.PERMISSIONS'),
]);

const canProceed = computed(() => {
  switch (currentStep.value) {
    case 1:
      return formData.value.name.trim().length > 0;
    case 2:
      return formData.value.columns.length > 0;
    case 3:
      return true; // Permissions are optional
    default:
      return false;
  }
});

const isLastStep = computed(() => currentStep.value === totalSteps);

const nextStep = () => {
  if (validateCurrentStep() && currentStep.value < totalSteps) {
    currentStep.value++;
  }
};

const prevStep = () => {
  if (currentStep.value > 1) {
    currentStep.value--;
  }
};

const validateCurrentStep = () => {
  errors.value = {};

  switch (currentStep.value) {
    case 1:
      if (!formData.value.name.trim()) {
        errors.value.name = t('KANBAN.PIPELINE_MODAL.VALIDATION.NAME_REQUIRED');
        return false;
      }
      break;
    case 2:
      if (formData.value.columns.length === 0) {
        errors.value.columns = t(
          'KANBAN.PIPELINE_MODAL.VALIDATION.STAGES_REQUIRED'
        );
        return false;
      }
      break;
  }

  return true;
};

const handleSave = () => {
  if (validateCurrentStep()) {
    emit('save', formData.value);
  }
};

const handleClose = () => {
  emit('close');
};

// Update columns positions when they change
watch(
  () => formData.value.columns,
  newColumns => {
    newColumns.forEach((column, index) => {
      column.position = index;
    });
  },
  { deep: true }
);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    @click.self="handleClose"
  >
    <div
      class="bg-n-background border border-n-weak rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col"
    >
      <!-- Header -->
      <div class="px-6 py-4 border-b border-n-weak">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-lg font-medium text-n-slate-12">
              {{
                isEdit
                  ? t('KANBAN.PIPELINE_MODAL.EDIT_TITLE')
                  : t('KANBAN.PIPELINE_MODAL.CREATE_TITLE')
              }}
            </h2>
            <p class="text-sm text-n-slate-11 mt-1">
              {{ stepTitles[currentStep - 1] }} ({{ currentStep }}/{{
                totalSteps
              }})
            </p>
          </div>
          <button
            class="text-n-slate-9 hover:text-n-slate-12 transition-colors"
            @click="handleClose"
          >
            <i class="i-lucide-x size-5" />
          </button>
        </div>

        <!-- Progress bar -->
        <div class="mt-4">
          <div class="flex items-center gap-2">
            <div
              v-for="step in totalSteps"
              :key="step"
              class="flex-1 h-2 rounded-full transition-colors duration-200"
              :style="{
                backgroundColor:
                  step <= currentStep ? '#3B82F6' : 'rgba(156, 163, 175, 0.3)',
              }"
            />
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto">
        <!-- Step 1: Basic Information -->
        <div v-if="currentStep === 1" class="p-6 space-y-6">
          <div>
            <h3 class="text-lg font-medium text-n-slate-12 mb-4">
              {{ t('KANBAN.PIPELINE_MODAL.BASIC_INFO.TITLE') }}
            </h3>
            <p class="text-sm text-n-slate-11 mb-6">
              {{ t('KANBAN.PIPELINE_MODAL.BASIC_INFO.SUBTITLE') }}
            </p>
          </div>

          <div class="space-y-4">
            <div>
              <Input
                v-model="formData.name"
                :label="t('KANBAN.PIPELINE_MODAL.BASIC_INFO.NAME_LABEL')"
                :placeholder="
                  t('KANBAN.PIPELINE_MODAL.BASIC_INFO.NAME_PLACEHOLDER')
                "
                :error="errors.name"
                required
              />
            </div>

            <div>
              <TextArea
                v-model="formData.description"
                :label="t('KANBAN.PIPELINE_MODAL.BASIC_INFO.DESCRIPTION_LABEL')"
                :placeholder="
                  t('KANBAN.PIPELINE_MODAL.BASIC_INFO.DESCRIPTION_PLACEHOLDER')
                "
                rows="3"
              />
            </div>
          </div>
        </div>

        <!-- Step 2: Stages -->
        <div v-else-if="currentStep === 2" class="p-6">
          <PipelineStepForm
            v-model="formData.columns"
            :error="errors.columns"
          />
        </div>

        <!-- Step 3: Permissions -->
        <div v-else-if="currentStep === 3" class="p-6">
          <PipelinePermissions v-model="formData.permissions" />
        </div>
      </div>

      <!-- Footer -->
      <div
        class="px-6 py-4 border-t border-n-weak flex items-center justify-between"
      >
        <div class="flex items-center gap-2">
          <NextButton
            v-if="currentStep > 1"
            variant="secondary"
            @click="prevStep"
          >
            <i class="i-lucide-arrow-left size-4" />
            {{ t('KANBAN.PIPELINE_MODAL.PREVIOUS') }}
          </NextButton>
        </div>

        <div class="flex items-center gap-2">
          <NextButton variant="secondary" @click="handleClose">
            {{ t('KANBAN.PIPELINE_MODAL.CANCEL') }}
          </NextButton>

          <NextButton
            v-if="!isLastStep"
            variant="primary"
            :disabled="!canProceed"
            @click="nextStep"
          >
            {{ t('KANBAN.PIPELINE_MODAL.NEXT') }}
            <i class="i-lucide-arrow-right size-4" />
          </NextButton>

          <NextButton
            v-else
            variant="primary"
            :disabled="!canProceed"
            @click="handleSave"
          >
            <i class="i-lucide-save size-4" />
            {{
              isEdit
                ? t('KANBAN.PIPELINE_MODAL.UPDATE')
                : t('KANBAN.PIPELINE_MODAL.CREATE')
            }}
          </NextButton>
        </div>
      </div>
    </div>
  </div>
</template>
